import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import ContactForm from '../components/ContactForm';

// Italian high-rises & new residential developments (Unsplash CDN)
const IMG_HERO = 'https://images.unsplash.com/photo-1624220484124-0e20c840ec23?auto=format&fit=crop&w=1600&q=80';
const IMG_TRANSPARENCY = 'https://images.unsplash.com/photo-1682368390812-1b9405741b25?auto=format&fit=crop&w=1400&q=80';
const IMG_MILANO = 'https://images.unsplash.com/photo-1624220484124-0e20c840ec23?auto=format&fit=crop&w=900&q=80&sat=-10';
const IMG_BOLOGNA = 'https://images.unsplash.com/photo-1531572753322-ad063cecc140?auto=format&fit=crop&w=900&q=80';
const IMG_TORINO = 'https://images.unsplash.com/photo-1741705619090-bc287a2e2187?auto=format&fit=crop&w=900&q=80';
const IMG_INTRO = 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1400&q=80';

function Faq() {
  const { t } = useTranslation();
  const keys = ['one', 'two', 'three', 'four', 'five'];
  const [open, setOpen] = useState('one');
  return (
    <ul className="auro-faq__list">
      {keys.map((k) => (
        <li key={k} className={open === k ? 'is-open' : ''}>
          <button type="button" onClick={() => setOpen(open === k ? '' : k)} aria-expanded={open === k}>
            <span>{t(`faq.items.${k}.q`)}</span>
            <em aria-hidden="true">{open === k ? '–' : '+'}</em>
          </button>
          <div className="auro-faq__ans"><p>{t(`faq.items.${k}.a`)}</p></div>
        </li>
      ))}
    </ul>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const currentLang = lang === 'en' ? 'en' : 'it';

  const goto = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      {/* HERO */}
      <section className="auro-hero">
        <div className="auro-hero__grid">
          <div className="auro-hero__text">
            <span className="auro-eyebrow">{t('hero.eyebrow')}</span>
            <h1 className="auro-h1">{t('hero.title')}</h1>
            <p className="auro-lead">{t('hero.subtitle')}</p>
            <div className="auro-hero__actions">
              <button type="button" className="auro-btn auro-btn--primary" onClick={goto('contact')} data-testid="hero-cta-primary">
                {t('hero.ctaPrimary')}
              </button>
              <button type="button" className="auro-btn auro-btn--ghost auro-btn--dark" onClick={goto('how')} data-testid="hero-cta-secondary">
                {t('hero.ctaSecondary')} →
              </button>
            </div>
            <dl className="auro-hero__stats">
              <div><dt>32+</dt><dd>{t('hero.stats.projects')}</dd></div>
              <div><dt>14</dt><dd>{t('hero.stats.cities')}</dd></div>
              <div><dt>18k+</dt><dd>{t('hero.stats.members')}</dd></div>
            </dl>
          </div>
          <div className="auro-hero__media">
            <div className="auro-hero__frame">
              <img src={IMG_HERO} alt="Milano skyline" loading="eager" />
            </div>
            <div className="auro-hero__badge">
              <span>Milano</span>
              <span>Bologna</span>
              <span>Torino</span>
              <span>Roma</span>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="auro-intro" id="intro">
        <div className="auro-container auro-intro__grid">
          <div className="auro-intro__media">
            <img src={IMG_INTRO} alt="Nuovi sviluppi residenziali in Italia" loading="lazy" />
          </div>
          <div>
            <span className="auro-eyebrow">{t('intro.eyebrow')}</span>
            <h2 className="auro-h2">{t('intro.title')}</h2>
            <p className="auro-body">{t('intro.body')}</p>
            <p className="auro-body">{t('intro.body2')}</p>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="auro-benefits" id="benefits">
        <div className="auro-container">
          <header className="auro-section__head">
            <span className="auro-eyebrow">{t('benefits.eyebrow')}</span>
            <h2 className="auro-h2">{t('benefits.title')}</h2>
          </header>
          <div className="auro-benefits__grid">
            {['barriers', 'digital', 'choice', 'impact'].map((k, idx) => (
              <article key={k} className="auro-card">
                <span className="auro-card__num">0{idx + 1}</span>
                <h3>{t(`benefits.items.${k}.title`)}</h3>
                <p>{t(`benefits.items.${k}.body`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="auro-how" id="how">
        <div className="auro-container">
          <header className="auro-section__head">
            <span className="auro-eyebrow">{t('how.eyebrow')}</span>
            <h2 className="auro-h2 auro-h2--light">{t('how.title')}</h2>
          </header>
          <ol className="auro-how__steps">
            {['one', 'two', 'three', 'four'].map((k) => (
              <li key={k}>
                <span className="auro-how__n">{t(`how.steps.${k}.n`)}</span>
                <h3>{t(`how.steps.${k}.title`)}</h3>
                <p>{t(`how.steps.${k}.body`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="auro-projects" id="projects">
        <div className="auro-container">
          <header className="auro-section__head">
            <span className="auro-eyebrow">{t('projects.eyebrow')}</span>
            <h2 className="auro-h2">{t('projects.title')}</h2>
          </header>
          <div className="auro-projects__grid">
            {[{k:'milano', img:IMG_MILANO},{k:'bologna', img:IMG_BOLOGNA},{k:'torino', img:IMG_TORINO}].map(({k, img}) => (
              <article key={k} className="auro-project">
                <div className="auro-project__img"><img src={img} alt={t(`projects.items.${k}.title`)} loading="lazy" /></div>
                <span className="auro-project__tag">{t('projects.tag')}</span>
                <div className="auro-project__body">
                  <p className="auro-project__city">{t(`projects.items.${k}.city`)}</p>
                  <h3>{t(`projects.items.${k}.title`)}</h3>
                  <p>{t(`projects.items.${k}.body`)}</p>
                  <button type="button" className="auro-linkish" onClick={goto('contact')}>
                    {t('cta.button')} →
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section className="auro-transparency" id="transparency">
        <div className="auro-container auro-transparency__grid">
          <div className="auro-transparency__text">
            <span className="auro-eyebrow">{t('transparency.eyebrow')}</span>
            <h2 className="auro-h2">{t('transparency.title')}</h2>
            <p className="auro-body">{t('transparency.body')}</p>
            <p className="auro-body">{t('transparency.body2')}</p>
            <ul className="auro-checklist">
              {['one','two','three','four'].map((k) => (
                <li key={k}><span aria-hidden="true">✓</span>{t(`transparency.points.${k}`)}</li>
              ))}
            </ul>
          </div>
          <div className="auro-transparency__media">
            <img src={IMG_TRANSPARENCY} alt="Riqualificazione urbana" loading="lazy" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="auro-faq" id="faq">
        <div className="auro-container auro-faq__grid">
          <header>
            <span className="auro-eyebrow">{t('faq.eyebrow')}</span>
            <h2 className="auro-h2">{t('faq.title')}</h2>
            <button type="button" className="auro-btn auro-btn--ghost auro-btn--dark" onClick={goto('contact')}>
              {t('cta.button')}
            </button>
          </header>
          <Faq />
        </div>
      </section>

      {/* CONTACT */}
      <section className="auro-contact" id="contact">
        <div className="auro-container auro-contact__grid">
          <div className="auro-contact__text">
            <span className="auro-eyebrow">{t('contact.eyebrow')}</span>
            <h2 className="auro-h2 auro-h2--light">{t('contact.title')}</h2>
            <p className="auro-body auro-body--light">{t('contact.subtitle')}</p>
            <div className="auro-contact__meta">
              <div>
                <p className="auro-contact__label">{t('contact.emailLabel')}</p>
                <a href="mailto:info@auronixio.com">{t('contact.emailAddress')}</a>
              </div>
              <div>
                <p className="auro-contact__label">{t('contact.addressLabel')}</p>
                <p>{t('contact.addressValue')}</p>
              </div>
            </div>
          </div>
          <div className="auro-contact__form">
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}
